/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from 'react';
import { IoMdClose } from 'react-icons/io';
import ReactToastify from '../../../customHook/useReactToastify';
import {
  useUpdateCandidateMutation,
  useLazyGetCandidatesByCompanyIdQuery,
} from '../../../features/apis/intergration';
import useCurrentUser from '../../../customHook/useCurrentUser';
import {
  Avatar,
  getDownloadParams,
  download_url,
  formatResumeDate,
} from './CandidateDetails';

const templateOptions = ['Shortlisted', 'Rejected', 'Call for Interview'];

const templateBodies: Record<string, string> = {
  Shortlisted: `Hi {{CAND_NAME}},

We reviewed your profile for {{ROLE_TITLE}} and would like to schedule an interview.

Screening Questions:
{{QUESTIONS}}

Document Requirements:
{{DOCS}}

Please reply with your availability (2 slots) and attach/share requested documents.

Thanks,
HR Team`,
  Rejected: `Hi {{CAND_NAME}},

Thank you for your interest in {{ROLE_TITLE}}. After careful consideration, we will not be moving forward at this time.

We appreciate your time and wish you all the best.

Thanks,
HR Team`,
  'Call for Interview': `Hi {{CAND_NAME}},

Congratulations! We'd like to invite you for an interview for the {{ROLE_TITLE}} position.

Please confirm your availability for the following slots:
{{QUESTIONS}}

Looking forward to speaking with you.

Thanks,
HR Team`,
};

const templateSubjects: Record<string, string> = {
  Shortlisted: 'Interview Invitation — {{ROLE_TITLE}}',
  Rejected: 'Application Update — {{ROLE_TITLE}}',
  'Call for Interview': 'Interview Call — {{ROLE_TITLE}}',
};

function TemplateDropdown({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (t: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full px-3 py-3 bg-[#F9F6FF] border border-slate-200 rounded-lg text-sm text-slate-700 hover:border-violet-400 transition-colors shadow-xl shadow-[#F8F5FF]"
      >
        <span className="text-slate-500 text-xs">
          Template:{' '}
          <span className="text-slate-700 font-medium">{selected}</span>
        </span>
        <svg
          className={`w-4 h-4 ml-2 text-slate-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-gradient-to-b from-[#6E3EF5] to-[#815FE2] text-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden z-50">
          {templateOptions.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                onSelect(opt);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-3 text-sm font-medium transition-colors hover:bg-[#815FE2]"
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CandidateAction({
  open,
  onClose,
  candidate,
}: {
  open: boolean;
  onClose: () => void;
  candidate: {
    status: string;
    _id: string;
    experienceYears: string;
    location: string;
    name: string;
    jobId: {
      jobTitle: string;
      questions: string[];
      documents: string[];
    };
    email: string;
    phone: string;
    skills: string[];

    resumes: {
      resumeUrl: string;
      uploadedAt: string;
    }[];
  };
}) {
  const [template, setTemplate] = useState('Shortlisted');
  const [emailBody, setEmailBody] = useState(templateBodies['Shortlisted']);
  const [updateCandidate, { isLoading }] = useUpdateCandidateMutation();
  const [getCandidates] = useLazyGetCandidatesByCompanyIdQuery();
  const user = useCurrentUser();
  const formatList = (items: string[] | undefined) => {
    if (!items || items.length === 0) return '';
    return items.map((item, i) => `${i + 1}. ${item}`).join('\n');
  };
  const handleSubmit = async () => {
    try {
      const payload = {
        candidateId: candidate._id,
        email: candidate.email,
        subject: templateSubjects[template].replace(
          '{{ROLE_TITLE}}',
          candidate.jobId.jobTitle
        ),
        body: emailBody,
        status: template,
      };
      await updateCandidate(payload).unwrap();
      await getCandidates(user?.companyId).unwrap();
      ReactToastify({
        success: true,
        message: 'Email sent successfully',
      });
      onClose();
    } catch (err) {
      console.error(err);
      ReactToastify({
        success: false,
        message: 'Error sending email',
      });
    }
  };
  const buildEmailBody = (templateKey: string) => {
    let body = templateBodies[templateKey]
      .replace('{{CAND_NAME}}', candidate.name)
      .replace('{{ROLE_TITLE}}', candidate.jobId.jobTitle);

    const questionsText = formatList(candidate.jobId.questions);
    const docsText = formatList(candidate.jobId.documents);
    if (questionsText) {
      body = body.replace('{{QUESTIONS}}', questionsText);
    } else {
      body = body.replace(/Screening Questions:\s*\{\{QUESTIONS\}\}\n?/i, '');
    }

    if (docsText) {
      body = body.replace('{{DOCS}}', docsText);
    } else {
      body = body.replace(/Document Requirements:\s*\{\{DOCS\}\}\n?/i, '');
    }

    return body;
  };

  useEffect(() => {
    if (candidate) {
      setTemplate('Shortlisted');
      setEmailBody(buildEmailBody('Shortlisted'));
    }
  }, [candidate]);

  const handleTemplateChange = (t: string) => {
    setTemplate(t);
    setEmailBody(buildEmailBody(t));
  };

  if (!open || !candidate) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-row">
        <div className="w-60 shrink-0 border-r border-b border-[#EEEEEE] p-2 flex flex-col items-center my-4 rounded-br-xl">
          <Avatar name={candidate?.name} />
          <h2 className="text-xl font-bold  text-center">{candidate.name}</h2>
          <p className="text-[10px] text-slate-500 text-center mt-1 leading-snug">
            {candidate.jobId.jobTitle}
          </p>
          <p className="text-[10px] text-slate-500 text-center mt-1 leading-snug">
            {candidate.location} · {candidate.experienceYears}
          </p>
          <div className="w-full mt-6 space-y-3 cursor-pointer">
            {candidate?.resumes?.map((r, i) => (
              <div
                key={i}
                onClick={() => {
                  const { docKey, fileName, fileType } = getDownloadParams(
                    r.resumeUrl
                  );

                  const downloadLink = `${download_url}?id=${docKey}&filename=${fileName}&filetype=${fileType}`;
                  window.open(downloadLink, '_blank');
                }}
                className="w-full bg-[#F9F6FF] rounded-xl  px-4 py-6 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              >
                <div>
                  <p className="text-sm font-semibold text-slate-700 group-hover:text-violet-600 transition-colors">
                    Resume
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {formatResumeDate(r.uploadedAt)}
                  </p>{' '}
                </div>
                <span className="text-[8px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-600">
                  Download
                </span>{' '}
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="space-y-1.5 my-4">
            <p className="text-sm text-slate-700">
              <span className="font-medium">Email:</span> {candidate.email}
            </p>
            <p className="text-sm text-slate-700">
              <span className="font-medium">Phone:</span> {candidate.phone}
            </p>
            <div className="flex gap-1.5 flex-wrap items-center">
              <span> Skills:</span>{' '}
              {candidate?.skills?.slice(0, 7).map((s) => (
                <span
                  key={s}
                  className="text-[10px] px-3 py-1 rounded-full bg-[#F1EDFF] text-[#272727]"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="flex gap-3 mb-5">
            <button className="px-6 py-2 rounded-full bg-[#6E3EF5] text-white text-sm font-semibold hover:bg-[#6E3EF5]/80 transition-colors shadow-sm">
              {candidate?.status}
            </button>
          </div>

          <hr className="border-[#D7D7D7] mb-4" />
          <div className="border border-[#EFEFEF] rounded-xl overflow-visible p-4">
            <div className="px-4 py-3 border-b border-slate-100 bg-[#F9F6FF] rounded-xl">
              <p className="text-xs text-slate-500 ">
                Subject:{' '}
                {templateSubjects[template].replace(
                  '{{ROLE_TITLE}}',
                  candidate.jobId.jobTitle
                )}
              </p>
            </div>
            <div className="py-2.5 border-b border-slate-100">
              <TemplateDropdown
                selected={template}
                onSelect={handleTemplateChange}
              />
            </div>

            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              rows={10}
              className="w-full px-4 py-3 text-sm text-slate-700 leading-relaxed resize-none outline-none focus:ring-2 focus:ring-blue-400 focus:ring-inset transition-all rounded-b-xl bg-[#F9F6FF] rounded-xl"
            />
          </div>
          <div className="px-4 py-3 flex justify-end border-t border-slate-100 bg-white rounded-b-xl">
            <div className="px-4 py-3 flex justify-end border-t border-slate-100 bg-white rounded-b-xl">
              <button
                onClick={handleSubmit}
                disabled={!template || isLoading}
                className={`px-6 py-2 rounded-full text-white text-sm font-semibold transition-colors shadow-sm
      ${
        !template || isLoading
          ? 'bg-[#6E3EF5]/50 cursor-not-allowed'
          : 'bg-[#6E3EF5] hover:bg-[#6E3EF5]/80'
      }
    `}
              >
                {isLoading ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full  text-slate-500 flex items-center justify-center text-lg transition-colors hover:text-red-500"
        >
          <IoMdClose />
        </button>
      </div>
    </div>
  );
}
